import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "col span-6" }
const _hoisted_2 = { class: "col span-6" }

import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { sortBy } from '@shell/utils/sort';
import LabeledSelect from '@shell/components/form/LabeledSelect.vue';
import { _VIEW } from '@shell/config/query-params';
import { EpinioConfiguration, EpinioService, EPINIO_TYPES, EPINIO_APP_MANIFEST } from '../../types';
import Application from '../../models/applications';

interface Props {
  initialApplication?: Application;
  application: Application;
  mode: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppConfiguration',
  props: {
    initialApplication: {},
    application: {},
    mode: {}
  },
  emits: ['change'],
  setup(__props: any, { emit: __emit }) {


const props = __props;
const emit = __emit;

const store = useStore();

const t = store.getters['i18n/t'];

const values = ref({
  configurations: [] as string[],
  services: [] as EpinioService[],
});

const fetchData = async () => {
  await Promise.all([
    store.dispatch('epinio/findAll', { type: EPINIO_TYPES.CONFIGURATION }),
    store.dispatch('epinio/findAll', { type: EPINIO_TYPES.SERVICE_INSTANCE }),
  ]);
};

onMounted(fetchData);

// Computed
const namespacedServices = computed(() =>
  (store.getters['epinio/all'](EPINIO_TYPES.SERVICE_INSTANCE) || [])
    .filter((s: EpinioService) => s.metadata.namespace === props.application.metadata.namespace)
);

const namespacedConfigurations = computed(() =>
  (store.getters['epinio/all'](EPINIO_TYPES.CONFIGURATION) || [])
    .filter((s: EpinioConfiguration) => s.metadata.namespace === props.application.metadata.namespace)
);

const configurations = computed(() => {
  const list = namespacedConfigurations.value
    .filter((s: EpinioConfiguration) => !s.isServiceRelated)
    .map((s: EpinioConfiguration) => ({
      label: s.metadata.name,
      value: s.metadata.name,
    }));

  return sortBy(list, 'label', false);
});

const services = computed(() => {
  const list = namespacedServices.value.map((s: EpinioService) => ({
    label: `${s.metadata.name} (${s.catalog_service})`,
    value: s,
  }));

  return sortBy(list, 'label', false);
});

const noConfigs = computed(() => !configurations.value.length);
const hasConfigs = computed(() => !!configurations.value.length);
const noServices = computed(() => !services.value.length);
const hasServices = computed(() => {
  return services.value.length > 0;
});
const isView = computed(() => props.mode === _VIEW);
const isFromManifest = computed(
  () => store.$router.currentRoute._value.query.from === EPINIO_APP_MANIFEST
);

// Watchers
watch(values, () => emit('change', values.value), { deep: true });

watch(noConfigs, (neu) => {
  if (neu && values.value.configurations?.length) {
    values.value.configurations = [];
  }
});

watch(noServices, (neu) => {
  if (neu && values.value.services?.length) {
    values.value.services = [];
  }
});

watch(hasConfigs, (neu, old) => {
  if (!old && neu) {
    if (props.initialApplication?.configuration?.configurations) {
      values.value.configurations = props.initialApplication.baseConfigurationsNames?.filter(
        (cc: string) => configurations.value.find((c: any) => c.value === cc)
      ) || [];
    }

    if (isFromManifest.value) {
      values.value.configurations = namespacedConfigurations.value
        .filter((nc: any) =>
          props.application.configuration.configurations.includes(nc.metadata.name) &&
          !nc.isServiceRelated
        )
        .map((nc: any) => nc.metadata.name) || [];
    }
  }
}, { immediate: true });

watch(hasServices, (neu, old) => {
    if (!old && neu) {
      if (props.initialApplication?.serviceConfigurationsNames) {
        values.value.services = props.initialApplication.services || [];
      }
    }

    if (isFromManifest.value) {
      const configurations = namespacedConfigurations.value
        .filter((nc: any) =>
          props.application.configuration.configurations.includes(nc.metadata.name) &&
          nc.isServiceRelated
        );

      values.value.services = services.value
        .filter((s: any) => configurations.some((d: any) => s.value.metadata.name === d.configuration.origin))
        .map((elem: any) => elem.value);
    }
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(LabeledSelect, {
        value: values.value.configurations,
        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((values.value.configurations) = $event)),
        "data-testid": "epinio_app-configuration_configurations",
        disabled: noConfigs.value || isView.value,
        options: configurations.value,
        searchable: true,
        mode: _ctx.mode,
        multiple: true,
        label: _unref(t)('typeLabel.configurations', { count: 2}),
        placeholder: noConfigs.value ? _unref(t)('epinio.applications.steps.configurations.configurations.select.placeholderNoOptions') : _unref(t)('epinio.applications.steps.configurations.configurations.select.placeholderWithOptions')
      }, null, 8, ["value", "disabled", "options", "mode", "label", "placeholder"])
    ]),
    _cache[2] || (_cache[2] = _createTextVNode()),
    _cache[3] || (_cache[3] = _createElementVNode("div", { class: "spacer" }, null, -1)),
    _cache[4] || (_cache[4] = _createTextVNode()),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(LabeledSelect, {
        value: values.value.services,
        "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((values.value.services) = $event)),
        "data-testid": "epinio_app-configuration_services",
        disabled: noServices.value || isView.value,
        options: services.value,
        searchable: true,
        mode: _ctx.mode,
        multiple: true,
        label: _unref(t)('epinio.applications.steps.configurations.services.select.label'),
        placeholder: noServices.value ? _unref(t)('epinio.applications.steps.configurations.services.select.placeholderNoOptions') : _unref(t)('epinio.applications.steps.configurations.services.select.placeholderWithOptions')
      }, null, 8, ["value", "disabled", "options", "mode", "label", "placeholder"])
    ])
  ]))
}
}

})